import { Controller } from "../../ts/controller";
import { TabSubmissionComment } from "../../ts/models/tab-submission-comment";
import { Logger } from "../../ts/utils/logger";
import { MarkdownUtils } from "../../ts/utils/markdown";

export class TabSubmissionChatPageController extends Controller {
    currentUserId: number | undefined = undefined;
    comments: TabSubmissionComment[] = [];
    submissionCommentPostUrl: string | undefined = undefined;
    submitting: boolean = false;
    error: string | null = null;

    constructor() {
        super({
            logLabel: 'TabSubmissionChatPageController',
        });
    }

    public initialize({
        submissionCommentPostUrl,
        currentUserId,
        initialComments,
    }: {
        submissionCommentPostUrl: string;
        currentUserId: number;
        initialComments: TabSubmissionComment[];
    }) {
        this.submissionCommentPostUrl = submissionCommentPostUrl;
        this.currentUserId = currentUserId;
        this.comments = initialComments;

        for (let comment of this.comments) {
            this._insertCommentHtml(comment);
        }

        this._initializeListeners();

        this._setState();
        Logger.info(this.logLabel, 'Initialized');
    }

    private _initializeListeners() {
        const inputTextBox = this.getElementByIdOrWarn('submission-comment-input');
        if (inputTextBox) {
            inputTextBox.addEventListener('input', () => {
                this._setState();
            });
        }
        this.bindClick({
            elementId: 'submission-comments-reload-button',
            handler: () => {
                location.reload();
            },
        });
        this.bindClick({
            elementId: 'create-submission-comment-button',
            handler: this._submitComment.bind(this),
        });
        this.bindClick({
            elementId: 'submission-comment-input-size-toggle',
            handler: () => {
                const inputElement = this.getElementByIdOrWarn('submission-comment-input');
                if (inputElement) {
                    inputElement.classList.toggle('expanded');
                }
            },
        });
    }

    private _setState() {
        const inputTextBox = this.getElementByIdOrWarn<HTMLInputElement>('submission-comment-input');
        const submitButton = this.getElementByIdOrWarn<HTMLButtonElement>('create-submission-comment-button');
        const errorText = this.getElementByIdOrWarn('submission-comment-error');
        let text = '';
        if (inputTextBox) {
            text = inputTextBox.value.trim();
        }
        if (submitButton) {
            submitButton.disabled = this.submitting || text.length === 0;
        }
        if (errorText) {
            if (this.error) {
                errorText.innerHTML = this.error;
                errorText.style.display = 'block';
            }
            else {
                errorText.innerHTML = '';
                errorText.style.display = 'none';
            }
        }
    }

    private _insertCommentHtml(comment: TabSubmissionComment) {
        const commentsContainer = this.getElementByIdOrWarn('submission-comments-container');
        if (commentsContainer) {
            const commentElement = document.createElement('div');
            commentElement.classList.add(
                'submission-comment',
                comment.user.id === this.currentUserId
                    ? 'user'
                    : 'other'
            );
            const commentMarkdown = MarkdownUtils.render(comment.text);
            commentElement.innerHTML = `
            <p class="submission-comment-by font-size-small">
            @${comment.user.username}
            </p>
            <div class="submission-comment-text-container markdown">
                ${commentMarkdown}
            </div>
        `;

            commentsContainer.appendChild(commentElement);
            commentsContainer.scrollTop = commentsContainer.scrollHeight;
        }
    }

    private async _submitComment() {
        const inputTextBox = this.getElementByIdOrWarn<HTMLInputElement>('submission-comment-input');
        if (inputTextBox) {
            const text = inputTextBox.value.trim();
            if (text.length === 0) {
                Logger.info(this.logLabel, 'Text was empty, not submitting');
                return;
            }
            const MIN_WAIT_TIME = 300;
            const startTime = performance.now();
            try {
                const commentData = {
                    text: text,
                };
                this.submitting = true;
                this.error = null;
                this._setState();
                const response = await fetch(this.submissionCommentPostUrl!, {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(commentData),
                });
                const elapsedTime = performance.now() - startTime;
                if (elapsedTime < MIN_WAIT_TIME) {
                    await new Promise((resolve) => setTimeout(resolve, MIN_WAIT_TIME - elapsedTime));
                }
                if (!response.ok) {
                    try {
                        const data = await response.json();
                        const errors = this.parseErrors(data);
                        this.error = errors.join('<br>');
                    }
                    catch (e) { }
                    throw Error('Something went wrong sending the comment');
                }
                const newCommentData = await response.json();
                this._insertCommentHtml(newCommentData);
                inputTextBox.value = '';
            }
            catch (e: unknown) {
                if (e instanceof Error) {
                    if (!this.error) {
                        this.error = e.message;
                    }
                }
                else {
                    Logger.error(this.logLabel, `An unexpected error occurred: ${e}`);
                }
            }
            finally {
                this.submitting = false;
                this._setState();
            }
        }
    }
}
